import { bool, mixed, number, object, string } from "yup";
import { dayjs } from "@thalamos/common";
import { DetainedStatuses, DischargeReason, isSection136Discharge, MhaStatus, } from "../types/patientState/PatientState.js";
import { formValidationErrorMessages, isoDateTimeString } from "./shared.js";
const baseManualEventSchema = object({
    status: string().required(),
    startDateTime: string()
        .optional()
        .when("status", {
        is: (status) => {
            const requiredFor = [
                MhaStatus.Section5_4,
                MhaStatus.Section5_2,
                MhaStatus.Section4,
                MhaStatus.Section136,
            ];
            return requiredFor.includes(status);
        },
        then: () => isoDateTimeString
            .nonNullable()
            .required("Please enter date and time of detention")
            .test("testEpisodeCurrentStartDateTimeTimeNotInFuture", "Cannot enter a date in the future", function checkCurrentStartDateTimeTimeNotInFuture(startDateTime) {
            return dayjs(startDateTime).isBefore(dayjs());
        }),
    })
        .when("status", {
        is: (status) => {
            const requiredFor = [MhaStatus.Section2];
            return requiredFor.includes(status);
        },
        then: () => isoDateTimeString
            .required("Please enter date of detention")
            .test("testEpisodeCurrentStartDateTimeTimeNotInFuture", "Cannot enter a date in the future", function checkCurrentStartDateTimeTimeNotInFuture(startDateTime) {
            return dayjs(startDateTime).isBefore(dayjs());
        }),
    })
        .when(["status", "hasRenewals"], {
        is: (status, hasRenewals) => {
            const requiredFor = [MhaStatus.Section17A, MhaStatus.Section3];
            return requiredFor.includes(status) && hasRenewals;
        },
        then: () => isoDateTimeString
            .required("Please enter date of detention")
            .test("testEpisodeCurrentStartDateTimeTimeNotInFuture", "Cannot enter a date more than two months in the future for the start date of a renewed Section 3 / CTO", function checkCurrentStartDateTimeTimeNotInFuture(startDateTime) {
            // Scenario: CTO or Section 3 renewal can be scheduled in the future
            // up until 2 months in advance
            return dayjs(startDateTime).isBefore(dayjs().add(2, "months"));
        }),
    }),
    initialStartDateTime: string().when("status", {
        is: (status) => {
            const requiredFor = [MhaStatus.Section17A, MhaStatus.Section3];
            return requiredFor.includes(status);
        },
        then: () => isoDateTimeString
            .required("Please enter date of detention")
            .test("testEpisodeCurrentStartDateTimeTimeNotInFuture", "Cannot enter a date in the future", function checkCurrentStartDateTimeTimeNotInFuture(initialStartDateTime, { parent }) {
            // If there are renewals or the status is Section 3, the initial start date must be in the past
            if (parent.hasRenewals || parent.status === MhaStatus.Section3) {
                return dayjs(initialStartDateTime).isBefore(dayjs());
            }
            else {
                // Scenario: Pending CTO scheduled in the future
                return dayjs(initialStartDateTime).isBefore(dayjs().add(2, "months"));
            }
        }),
        otherwise: () => string().optional(),
    }),
    episodeOfCareDischargeReason: string().when("status", {
        is: (status) => {
            const requiredFor = [MhaStatus.NotDetained];
            return requiredFor.includes(status);
        },
        then: (schema) => schema
            .required("Please select a reason")
            .oneOf(Object.values(DischargeReason)),
        otherwise: () => string().optional(),
    }),
    episodeOfCareDischargeDateTime: string()
        .optional()
        .when("status", {
        is: (status) => {
            const requiredFor = [MhaStatus.NotDetained];
            return requiredFor.includes(status);
        },
        then: () => isoDateTimeString
            .required("Please enter date and time of discharge ")
            .test("testNotDetainedCurrentDischargeDateTimeTimeNotInFuture", "Cannot enter a date in the future", function checkCurrentStartDateTimeTimeNotInFuture(episodeOfCareDischargeDateTime) {
            return dayjs(episodeOfCareDischargeDateTime).isBefore(dayjs());
        }),
    }),
});
const validManualEventMhaStatuses = DetainedStatuses.concat(MhaStatus.Unknown);
const statusesRequringHospital = [
    MhaStatus.Section17A,
    MhaStatus.Section3,
    MhaStatus.Section5_4,
    MhaStatus.Section5_2,
    MhaStatus.Section4,
    MhaStatus.Section2,
    MhaStatus.NotDetained,
];
const requiresHospital = (parent) => parent.status !== undefined &&
    statusesRequringHospital.includes(parent.status) &&
    !isSection136Discharge(parent.status, parent.episodeOfCareDischargeReason);
const hospitalSchema = object()
    .optional()
    .shape({
    name: string()
        .trim()
        .max(255, formValidationErrorMessages.tooManyCharactersError)
        .optional(),
    address: string()
        .trim()
        .max(255, formValidationErrorMessages.tooManyCharactersError)
        .optional(),
    postalCode: string()
        .trim()
        .max(8, formValidationErrorMessages.postalCodeCharacterError)
        .optional(),
    odsCode: string().optional(),
    id: string().optional(),
})
    .test("testHospitalNameSpecified", "Hospital name is required", function checkHospital(hospital, testContext) {
    if (requiresHospital(this.parent)) {
        if (!hospital || !hospital.name) {
            return testContext.createError({
                path: "hospital.name",
                message: "Hospital name is required",
            });
        }
    }
    return true;
})
    .test("testHospitalAddressSpecified", "Hospital address is required", function checkHospital(hospital, testContext) {
    if (requiresHospital(this.parent)) {
        if (!hospital || !hospital.address) {
            return testContext.createError({
                path: "hospital.address",
                message: "Hospital address is required",
            });
        }
    }
    return true;
})
    .test("testHospitalPostalcodeSpecified", "Hospital postalCode is required", function checkHospital(hospital, testContext) {
    if (requiresHospital(this.parent)) {
        if (!hospital || !hospital.postalCode) {
            return testContext.createError({
                path: "hospital.postalCode",
                message: "Hospital postalCode is required",
            });
        }
    }
    return true;
});
export const s136PlaceOfSafetyTypeValues = [
    "hbPos136",
    "emergencyDepartment",
    "returnedToMentalHealthUnit",
    "careHome",
    "custody",
    "policeStation",
    "privateHome",
    "other",
];
const section136ContextSchema = object()
    .optional()
    .shape({
    placeOfSafety: object()
        .optional()
        .shape({
        id: string().optional(),
        name: string().optional(),
        address: string().optional(),
        postalCode: string().optional(),
        odsCode: string().optional(),
        type: string().oneOf(s136PlaceOfSafetyTypeValues).optional(),
        trustId: string().optional(),
        trustName: string().optional(),
    }),
    policeContext: object().optional().shape({
        forceName: string().optional(),
        visionOrgId: string().optional(),
        officerName: string().optional(),
    }),
})
    .test("testSection136ContextRequiredForS136", "Place of safety details are required for Section 136", function checkSection136Context(section136Context, testContext) {
    const status = this.parent.status;
    if (status !== MhaStatus.Section136)
        return true;
    const pos = section136Context?.placeOfSafety;
    if (!pos?.name || !pos?.address || !pos?.postalCode) {
        return testContext.createError({
            path: "section136Context.placeOfSafety.name",
            message: "Place of safety name, address, and postcode are required",
        });
    }
    if (!pos?.trustId) {
        return testContext.createError({
            path: "section136Context.placeOfSafety.trustId",
            message: "Please select a trust for the place of safety",
        });
    }
    return true;
});
const previousPlaceOfSafetySchema = object()
    .optional()
    .shape({
    id: string().optional(),
    name: string().optional(),
    address: string().optional(),
    postalCode: string().optional(),
    odsCode: string().optional(),
    type: string().oneOf(s136PlaceOfSafetyTypeValues).optional(),
    trustId: string().optional(),
    trustName: string().optional(),
})
    .test("testPreviousPlaceOfSafetyRequiredForSection136Discharge", "Place of safety details are required for a Section 136 discharge", function checkPreviousPlaceOfSafety(previousPlaceOfSafety, testContext) {
    const { status, episodeOfCareDischargeReason } = this.parent;
    if (!isSection136Discharge(status, episodeOfCareDischargeReason)) {
        return true;
    }
    if (!previousPlaceOfSafety?.name ||
        !previousPlaceOfSafety?.address ||
        !previousPlaceOfSafety?.postalCode) {
        return testContext.createError({
            path: "previousPlaceOfSafety.name",
            message: "Place of safety name, address, and postcode are required",
        });
    }
    if (!previousPlaceOfSafety?.trustId) {
        return testContext.createError({
            path: "previousPlaceOfSafety.trustId",
            message: "Please select a trust for the place of safety",
        });
    }
    return true;
});
export const manualEventSchema = baseManualEventSchema.shape({
    status: mixed().oneOf(validManualEventMhaStatuses).required(),
    hospital: hospitalSchema,
    section136Context: section136ContextSchema,
    previousPlaceOfSafety: previousPlaceOfSafetySchema,
    type: string().required().oneOf(["manualEvent"]),
    numberOfRenewals: number().when("status", {
        is: (status) => {
            const requiredFor = [MhaStatus.Section17A, MhaStatus.Section3];
            return requiredFor.includes(status);
        },
        then: (schema) => schema.required("Enter number of renewals for CTO"),
        otherwise: () => number().optional(),
    }),
    // Section 136 manual extension hours (1-12). Range enforced in the controller.
    section136ExtensionHours: number().optional(),
});
// Top-level place-of-safety form field, for a Section 136 and for the
// discharge that ends one. A trust is always required for a Section 136 POS.
const formPlaceOfSafetySchema = object()
    .optional()
    .shape({
    name: string().optional(),
    address: string().optional(),
    postalCode: string().optional(),
    odsCode: string().optional(),
    isConfirmed: bool().optional(),
    placeOfSafetyId: string().optional(),
    trustId: string().optional(),
    trustName: string().optional(),
})
    .test("testPlaceOfSafetyRequiredForS136", "Place of safety details are required for Section 136", function checkPlaceOfSafety(placeOfSafety, testContext) {
    const { status, episodeOfCareDischargeReason } = this.parent;
    if (status !== MhaStatus.Section136 &&
        !isSection136Discharge(status, episodeOfCareDischargeReason)) {
        return true;
    }
    if (!placeOfSafety?.name) {
        return testContext.createError({
            path: "placeOfSafety.name",
            message: "Please enter Place of Safety name",
        });
    }
    if (!placeOfSafety?.address) {
        return testContext.createError({
            path: "placeOfSafety.address",
            message: "Please enter Place of Safety address",
        });
    }
    if (!placeOfSafety?.postalCode) {
        return testContext.createError({
            path: "placeOfSafety.postalCode",
            message: "Please enter Place of Safety postcode",
        });
    }
    if (!placeOfSafety?.isConfirmed) {
        return testContext.createError({
            path: "placeOfSafety.isConfirmed",
            message: "Please confirm the name and address is correct",
        });
    }
    if (!placeOfSafety?.trustId) {
        return testContext.createError({
            path: "placeOfSafety.trustId",
            message: "Please select a trust for the place of safety",
        });
    }
    return true;
});
export const formManualEventSchema = baseManualEventSchema.shape({
    placeOfSafety: formPlaceOfSafetySchema,
    hospital: hospitalSchema.concat(object().shape({
        isConfirmed: bool()
            .optional()
            .default(false)
            .when("address", {
            is: (a) => a,
            then: (s) => s.oneOf([true], "Address confirmation is required"),
            otherwise: (s) => s,
        }),
    })),
    hasRenewals: bool().when("status", {
        is: (status) => {
            const requiredFor = [MhaStatus.Section17A, MhaStatus.Section3];
            return requiredFor.includes(status);
        },
        then: (schema) => schema.required("Please confirm if a renewal has taken place"),
        otherwise: () => string().optional(),
    }),
    numberOfRenewals: number().when(["status", "hasRenewals"], {
        is: (status, hasRenewals) => {
            const requiredFor = [MhaStatus.Section17A, MhaStatus.Section3];
            return requiredFor.includes(status) && hasRenewals;
        },
        then: (schema) => schema.required("Enter number of renewals for CTO"),
        otherwise: () => number().optional(),
    }),
    expiryDateTime: string()
        .optional()
        .test("testExpiryDateTimeNotInPast", "Cannot enter an expiry date in the past", function checkExpiryDateTimeNotInPast(expiryDateTime) {
        const { status, expiryOverrideDateTime } = this.parent;
        // All statuses with an expiry should have an expiry in the future
        const requiredFor = [
            MhaStatus.Section17A,
            MhaStatus.Section3,
            MhaStatus.Section5_4,
            MhaStatus.Section5_2,
            MhaStatus.Section4,
            MhaStatus.Section2,
            MhaStatus.Section136,
        ];
        if (requiredFor.includes(status)) {
            const overriddenExpiry = expiryOverrideDateTime ?? expiryDateTime;
            return dayjs(overriddenExpiry).isAfter(dayjs());
        }
        return true;
    })
        .test("testExpiryOverride", "Override of expiry date must be later than default expiry", function checkOverride(expiryDateTime) {
        const { expiryOverrideDateTime } = this.parent;
        if (expiryOverrideDateTime) {
            return dayjs(expiryOverrideDateTime).isAfter(dayjs(expiryDateTime));
        }
        return true;
    }),
    expiryOverrideDateTime: isoDateTimeString.optional(),
    expiryOverrideReason: string().when("expiryOverrideDateTime", {
        is: (value) => value && dayjs(value).isValid(),
        then: (schema) => schema.required("Please provide a reason for overriding the expiry date"),
        otherwise: () => string().optional(),
    }),
    expiryOverrideReasonDescription: string().when("expiryOverrideDateTime", {
        is: (value) => value && dayjs(value).isValid(),
        then: (schema) => schema.required("Please provide a reason description for overriding the expiry date"),
        otherwise: () => string().optional(),
    }),
    hasSection136Extension: bool().when("status", {
        is: (status) => status === MhaStatus.Section136,
        then: (schema) => schema.required("Please confirm if the Section 136 has been extended."),
        otherwise: () => bool().optional(),
    }),
    section136ExtensionHours: number().when("hasSection136Extension", {
        is: (hasSection136Extension) => hasSection136Extension === true,
        then: (schema) => schema
            .required("Please confirm how long the Section 136 has been extended. Maximum 12 hours")
            .min(1, "Please confirm how long the Section 136 has been extended. Maximum 12 hours")
            .max(12, "Please confirm how long the Section 136 has been extended. Maximum 12 hours"),
        otherwise: () => number().optional(),
    }),
});
//# sourceMappingURL=patientStateEvent.js.map